import React, {useState, useEffect} from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PlayCircleOutline, 
  Description, 
  Assignment, 
  Quiz, 
  CheckCircle,
  Lock,
  ArrowForward,
  CircleOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { useUser } from '../../context/UserContext';
import { useToast } from '../../context/ToastContext';
import axios from 'axios';
import EmptyState from '../common/EmptyState';

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const ModuleItem = styled(ListItem)(({ theme, completed, isLast, locked }) => ({
  marginBottom: theme.spacing(1),
  //backgroundColor: completed ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
  '&:hover': {
    //backgroundColor: locked ? 'transparent' : (completed ? 'rgba(76, 175, 80, 0.12)' : 'rgba(0, 0, 0, 0.04)'),
    backgroundColor:'rgba(0, 0, 0, 0.04)',
    //boxShadow: locked ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.05)',
  },
  //border: '1px solid #bbb',
  borderRadius: '8px',
  marginBottom: '10px',
  cursor: locked ? 'default' : 'pointer',
  opacity: locked ? 0.8 : 1,
  position: 'relative',
}));

const LockOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.03)',
  zIndex: 1,
  borderRadius: '8px',
}));

const CourseModules = ({ 
  activities, 
  courseId, 
  progress, 
  enrolled, 
  isAdminOrAuthor = false,
  showLockStatus = false,
  prerequisitesMet = true,
  requiresApproval = false,
  contentOrdered = 0,
  refreshOnClose = null
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { openContentPlayer } = useContentPlayer();
  const { showToast } = useToast();
  
  // Initialize completedActivities from progress prop
  const [completedActivities, setCompletedActivities] = useState([]);
  // Add this state and function to the component
  const [refreshKey, setRefreshKey] = useState(0);

  const forceRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };
  // Update completedActivities when progress changes
  useEffect(() => {
    if (progress && Array.isArray(progress.completed_cont_arr)) {
      console.log('Setting completed activities from progress:', progress.completed_cont_arr);
      // Make sure we're working with numbers, not strings
      const completedIds = progress.completed_cont_arr.map(id => 
        typeof id === 'string' ? parseInt(id, 10) : id
      );
      setCompletedActivities(completedIds);
    } else if (progress && typeof progress.completed_cont_arr === 'string') {
      // Handle case where completed_cont_arr might be a comma-separated string
      const completedIds = progress.completed_cont_arr.split(',')
        .map(id => parseInt(id.trim(), 10))
        .filter(id => !isNaN(id));
      console.log('Parsed completed activities from string:', completedIds);
      setCompletedActivities(completedIds);
    } else {
      console.log('No completed activities in progress data or invalid format');
      setCompletedActivities([]);
    }
    forceRefresh();
  }, [progress]);

  const getContentIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'video':
        return <PlayCircleOutline />;
      case 'document':
        return <Description />;
      case 'assignment':
        return <Assignment />;
      case 'quiz':
        return <Quiz />;
      default:
        return <Description />;
    }
  }; 

  const isOrderedCourse = contentOrdered === '1' || contentOrdered === 1 || contentOrdered === true;

  const isActivityLocked = (activity, index, allActivities) => {
    if (!enrolled || isAdminOrAuthor || !isOrderedCourse || index === 0) {
      return false;
    }

    const previousItems = allActivities.slice(0, index);
    return previousItems.some(prevActivity => !isCompleted(prevActivity.ref_id));
  };

  const handleModuleClick = (activity, index, allActivities) => {
    const isLocked = isActivityLocked(activity, index, allActivities);

    // If the module is locked and user is not enrolled, show message
    if (showLockStatus && !isAdminOrAuthor) {
      if (!prerequisitesMet) {
        showToast('You must complete all prerequisites before enrolling in this course', 'warning');
      } else if (requiresApproval) {
        showToast('This course requires manager approval. Please request enrollment.', 'info');
      } else {
        showToast('Please enroll in this course to access the content', 'info');
      }
      return;
    }

    if (isLocked) {
      showToast('Please complete the previous content first', 'warning');
      return;
    }
    
    // For admins/authors or enrolled users, open the content
    if (isAdminOrAuthor || enrolled) {
      const clrId = activity.clr_id ? activity.clr_id : null;
    
      openContentPlayer(
        courseId, 
        activity.ref_id, 
        `/courses/${courseId}`,
        enrolled ? async (contentId) => {
            
          // Convert contentId to number if it's a string
          const numericId = typeof contentId === 'string' ? parseInt(contentId, 10) : contentId;
          
          // Update local state to mark this content as completed
          setCompletedActivities(prev => {
            if (!prev.includes(numericId)) {
              return [...prev, numericId];
            }
            return prev;
          });
        } : null,
        // Set learner mode to true for enrolled users, false for admins/authors
        enrolled && !isAdminOrAuthor,
        clrId,
        null,
        refreshOnClose
      );
    }
  };
    
  const isCompleted = (activityId) => {
    // Convert activityId to number if it's a string
    const numericId = typeof activityId === 'string' ? parseInt(activityId, 10) : activityId;
    
    // Check if the ID exists in completedActivities
    return completedActivities.includes(numericId);
  };

  // Get lock message based on course conditions
  const getLockMessage = () => {
    if (!prerequisitesMet) return "Complete prerequisites first";
    if (requiresApproval) return "Requires manager approval";
    return "Enroll to access";
  };

  return (
    <SectionPaper elevation={0}>
      <Typography variant="h5" component="h2" gutterBottom>
        Course Modules
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      {activities && activities.length > 0 ? (
        <List>
          {activities.map((activity, index) => {
            const completed = isCompleted(activity.ref_id);
            const isLast = index === activities.length - 1;
            const isLocked = showLockStatus && !isAdminOrAuthor ? true : isActivityLocked(activity, index, activities);
            
            return (
              <ModuleItem 
                key={activity.ref_id}
                onClick={() => handleModuleClick(activity, index, activities)}
                completed={isCompleted(activity.ref_id) ? 1 : 0}                
                isLast={index === activities.length - 1}
                locked={isLocked}
              >
                {isLocked && (
                  <LockOverlay>
                    <Lock sx={{ fontSize: 40, opacity: 0.3, color: 'action.disabled' }} />
                  </LockOverlay>
                )}
                {/*
                <ListItemIcon sx={{ zIndex: 2 }}>
                  {getContentIcon(activity.type)}
                </ListItemIcon> */}
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    backgroundColor: '#F1EFE8',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    fontSize: 13,
                    fontWeight: 600,
                    mr: 2,
                    color: 'rgba(22,21,15,.50)',                      
                  }}
                >
                  M
                </Box>
                <ListItemText 
                  primary={activity.name}
                  //secondary={`${activity.type?.charAt(0).toUpperCase() + activity.type?.slice(1) || 'Content'}`}
                  secondary={/* '1 min', not '1 mins' (N44 pt 11) */ `${activity.duration ? `${activity.duration} ${Number(activity.duration) === 1 ? 'min' : 'mins'}` : ''}`}
                  sx={{ zIndex: 2 }}
                />
                <ListItemSecondaryAction sx={{ zIndex: 2 }}>
                  {completed ? (
                    <Tooltip title="Completed">
                      <CheckCircle color="success" />
                    </Tooltip>
                  ) : isLocked ? (
                    <Tooltip title={getLockMessage()}>
                      <Chip
                        icon={<Lock fontSize="small" />}
                        label={getLockMessage()}
                        size="small"
                        variant="outlined"
                        color={!prerequisitesMet ? "warning" : "default"}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Start">
                      <CircleOutlined />
                      {/*
                      <IconButton edge="end" onClick={(e) => {
                        e.stopPropagation();
                        handleModuleClick(activity, index, activities);
                      }}>
                        <ArrowForward />
                      </IconButton>
                      */}
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ModuleItem>
            );
          })}
        </List>
      ) : (
        <>
          {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
          <EmptyState framed={false} title="No content available for this course." message={null} />
        </>
      )}
      
      {showLockStatus && !isAdminOrAuthor && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px dashed #bbb' }}>
          <Typography variant="subtitle1" gutterBottom>
            <Lock fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
            Content Access Restricted
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {!prerequisitesMet ? (
              "You need to complete all prerequisites before you can enroll in this course."
            ) : requiresApproval ? (
              "This course requires manager approval. Please request enrollment to access content."
            ) : (
              "Enroll in this course to access all content and track your progress."
            )}
          </Typography>
        </Box>
      )}
    </SectionPaper>
  );
};

export default CourseModules;